/**
 * 일일 리포트 API 클라이언트
 * 일일 리포트 API 클라이언트
 */
import { API_BASE_URL as API_URL } from '../config';
import { apiFetch } from '../utils/apiFetch';
import type { PageResponse } from '../types/pagination';

/**
 * 리포트 목록 항목 타입
 */
export interface ReportSummary {
  date: string;
  scope?: 'live' | 'paper';
  total_value: number;
  daily_return: number;
  trades_count: number;
  positions_count: number;
}

/**
 * 계좌 정보 타입
 */
export interface AccountInfo {
  total_value: number;
  cash: number;
  buying_power: number;
  daily_return_percent: number;
  daily_return_amount: number;
}

/**
 * 포지션 정보 타입
 */
export interface Position {
  symbol: string;
  asset_type: 'stock' | 'crypto';
  qty: number;
  market_value: number;
  unrealized_pl_percent: number;
  unrealized_pl: number;
}

/**
 * 거래 정보 타입
 */
export interface Trade {
  time: string;
  side: 'BUY' | 'SELL';
  symbol: string;
  qty: number;
  price: number;
  profit_percent: number | null;
}

/**
 * 리포트 상세 타입
 */
export interface ReportDetail {
  date: string;
  scope?: 'live' | 'paper';
  account: AccountInfo;
  positions: Position[];
  trades: Trade[];
}

/**
 * 일일 리포트 목록 조회
 */
export const getReportsList = async (days: number = 7): Promise<ReportSummary[]> => {
  const response = await apiFetch(`${API_URL}/api/reports/list?days=${days}`);

  if (!response.ok) {
    throw new Error('리포트 목록 조회 실패');
  }

  return response.json();
};

export interface ReportListQuery {
  limit: number;
  offset: number;
  startDate?: string;
  endDate?: string;
  days?: number;
  scope?: 'live' | 'paper';
}

export const getReportsPage = async (
  query: ReportListQuery
): Promise<PageResponse<ReportSummary>> => {
  const params = new URLSearchParams();
  params.append('limit', String(query.limit));
  params.append('offset', String(query.offset));
  if (query.startDate) params.append('start_date', query.startDate);
  if (query.endDate) params.append('end_date', query.endDate);
  if (query.days) params.append('days', String(query.days));
  if (query.scope) params.append('scope', query.scope);

  const response = await apiFetch(`${API_URL}/api/reports/list?${params}`);
  if (!response.ok) {
    throw new Error('리포트 목록 조회 실패');
  }

  return response.json();
};

/**
 * 리포트 상세 조회
 */
export const getReportDetail = async (date: string): Promise<ReportDetail> => {
  const response = await apiFetch(`${API_URL}/api/reports/detail/${date}`);

  if (!response.ok) {
    throw new Error(`리포트 상세 조회 실패: ${date}`);
  }

  return response.json();
};

/**
 * 오늘 리포트 조회
 */
export const getTodayReport = async (): Promise<ReportDetail> => {
  const response = await apiFetch(`${API_URL}/api/reports/today`);

  if (!response.ok) {
    throw new Error('오늘 리포트 조회 실패');
  }

  return response.json();
};
